import { FluzElementsError } from "./errors.js";
import { resolveFrameHostOrigin } from "./frameHostOrigin.js";
import { mountFrame } from "./frameMount.js";
import type { FrameMount } from "./frameMount.js";
import type { StyleAllowlist } from "./style.js";

export interface FrameFieldConfig {
  field: string;
  // Whether reveal(field) may target this field on its own, independent of
  // reveal() revealing every mounted field together. Defaults to true.
  individualReveal?: boolean;
}

export type FrameField = string | FrameFieldConfig;

export interface FrameFieldViewerOptions {
  clientToken: string;
  loadToken: string;
  frameHostOrigin?: string;
  fields?: FrameField[];
  mountId?: string;
  style?: StyleAllowlist;
  mountTimeoutMs?: number;
}

export interface FrameFieldViewer {
  mount(target: HTMLElement): Promise<void>;
  reveal(field?: string): Promise<void>;
  setMask(field: string, masked: boolean, options?: { hidden?: boolean }): void;
  onError(handler: (error: FluzElementsError) => void): () => void;
  onMount(handler: () => void): () => void;
  destroy(): void;
}

function generateId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeFieldConfig(entry: FrameField): FrameFieldConfig {
  return typeof entry === "string" ? { field: entry, individualReveal: true } : { individualReveal: true, ...entry };
}

// Shared by createCardViewer and createAccountViewer -- both mount one iframe
// per field against the same frame-host protocol (handshake, reveal,
// set-mask), differing only in which field names are valid for their
// reveal purpose. `viewerName` is used only in error messages.
export function createFrameFieldViewer(
  viewerName: string,
  defaultFields: string[],
  options: FrameFieldViewerOptions,
): FrameFieldViewer {
  const fieldConfigs = (options.fields ?? defaultFields).map(normalizeFieldConfig);
  const allowedFields = new Set(defaultFields);
  const invalidField = fieldConfigs.find((config) => !allowedFields.has(config.field));
  if (invalidField) {
    throw new FluzElementsError("INVALID_FIELD", `Unknown field: "${invalidField.field}"`);
  }
  const fields = fieldConfigs.map((config) => config.field);
  const individualRevealByField = new Map(fieldConfigs.map((config) => [config.field, config.individualReveal !== false]));

  const frameHostOrigin = resolveFrameHostOrigin(options.frameHostOrigin);
  const baseMountId = options.mountId ?? generateId();

  const errorHandlers = new Set<(error: FluzElementsError) => void>();
  const mountHandlers = new Set<() => void>();
  const mounts = new Map<string, FrameMount>();
  const mountedFields = new Set<string>();
  let mounted = false;
  let mountNotified = false;

  function emitError(error: FluzElementsError) {
    errorHandlers.forEach((handler) => handler(error));
  }

  function noteFieldMounted(field: string) {
    mountedFields.add(field);
    if (!mountNotified && mountedFields.size === fields.length) {
      mountNotified = true;
      mountHandlers.forEach((handler) => handler());
    }
  }

  return {
    async mount(target: HTMLElement) {
      if (mounted) {
        throw new FluzElementsError("MOUNT_FAILED", `${viewerName} is already mounted.`);
      }
      mounted = true;

      mountedFields.clear();
      mountNotified = false;

      const results = await Promise.allSettled(
        fields.map(async (field) => {
          const url = new URL(`/v1/frame/${field}`, frameHostOrigin);
          // loadToken (not clientToken) travels in the query string: it's
          // short-lived and scoped only to resolving the grant for this GET
          // (frame-host needs that pre-render, for the frame-ancestors CSP
          // header -- see frame-host routes/frame.ts). It can't authenticate
          // reveal/collect/payment-method, so a Referer/log/DOM leak of the
          // URL isn't a reveal-capable credential. The actual bearer
          // (clientToken) never touches the URL -- it's handed over below via
          // the handshake postMessage instead.
          url.searchParams.set("token", options.loadToken);

          const mount = await mountFrame({
            mountId: `${baseMountId}-${field}`,
            frameUrl: url.toString(),
            frameOrigin: frameHostOrigin,
            container: target,
            style: options.style,
            mountTimeoutMs: options.mountTimeoutMs,
            onError: emitError,
            handshakeExtra: { clientToken: options.clientToken },
            onMessage: (message) => {
              if (message.type === "mount") {
                noteFieldMounted(field);
              }
            },
          });

          mounts.set(field, mount);
        }),
      );

      const failure = results.find((result): result is PromiseRejectedResult => result.status === "rejected");
      if (failure) {
        mounts.forEach((mount) => mount.destroy());
        mounts.clear();
        mounted = false;
        throw failure.reason instanceof FluzElementsError
          ? failure.reason
          : new FluzElementsError("MOUNT_FAILED", "Failed to mount one or more fields.");
      }
    },

    async reveal(field?: string) {
      if (!mounted) {
        throw new FluzElementsError("MOUNT_FAILED", `${viewerName} is not mounted.`);
      }

      if (field === undefined) {
        mounts.forEach((mount) => mount.send("reveal", { trigger: "button" }));
        return;
      }

      const mount = mounts.get(field);
      if (!mount) {
        throw new FluzElementsError("MOUNT_FAILED", `Unknown field: ${field}`);
      }
      if (!individualRevealByField.get(field)) {
        throw new FluzElementsError("INDIVIDUAL_REVEAL_DISABLED", `Field "${field}" is not enabled for individual reveal.`);
      }
      mount.send("reveal", { trigger: "button" });
    },

    setMask(field: string, masked: boolean, maskOptions?: { hidden?: boolean }) {
      const mount = mounts.get(field);
      if (!mount) {
        throw new FluzElementsError("MOUNT_FAILED", `Unknown field: ${field}`);
      }
      mount.send("set-mask", { masked, hidden: maskOptions?.hidden ?? false });
    },

    onError(handler: (error: FluzElementsError) => void) {
      errorHandlers.add(handler);
      return () => errorHandlers.delete(handler);
    },

    onMount(handler: () => void) {
      mountHandlers.add(handler);
      return () => mountHandlers.delete(handler);
    },

    destroy() {
      mounts.forEach((mount) => mount.destroy());
      mounts.clear();
      mounted = false;
      mountedFields.clear();
      mountNotified = false;
    },
  };
}
